import React from "react";
import { TRACK } from "../lib/i18n";
import { Layers, Plug, Bot, Search, Activity, GraduationCap } from "lucide-react";

const ICONS = [Layers, Plug, Bot, Search, Activity, GraduationCap];

export default function Skills({ t }) {
  const items = t.skills.items;

  return (
    <section
      id="skills"
      data-testid="skills-section"
      className="border-b border-rule"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-ember">
              ✦ {t.skills.kicker}
            </div>
            <h2 className="mt-3 font-serif text-4xl md:text-6xl leading-[0.98] tracking-tight max-w-3xl">
              {t.skills.title}
            </h2>
          </div>
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-mist">
            06 / capabilities
          </div>
        </div>

        {/* Asymmetric Tetris grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* 01 — Big tile */}
          <Tile
            data-testid={`skill-tile-0`}
            className="md:col-span-7 md:row-span-2 min-h-[280px] md:min-h-[360px]"
            item={items[0]}
            Icon={ICONS[0]}
            big
          />
          {/* 02 */}
          <Tile
            data-testid={`skill-tile-1`}
            className="md:col-span-5"
            item={items[1]}
            Icon={ICONS[1]}
          />
          {/* 03 */}
          <Tile
            data-testid={`skill-tile-2`}
            className="md:col-span-5"
            item={items[2]}
            Icon={ICONS[2]}
          />
          {/* 04 */}
          <Tile
            data-testid={`skill-tile-3`}
            className="md:col-span-4"
            item={items[3]}
            Icon={ICONS[3]}
          />
          {/* 05 — Sprint with image */}
          <Tile
            data-testid={`skill-tile-4`}
            className="md:col-span-4 relative overflow-hidden"
            item={items[4]}
            Icon={ICONS[4]}
            bgImage={TRACK}
            dark
          />
          {/* 06 */}
          <Tile
            data-testid={`skill-tile-5`}
            className="md:col-span-4"
            item={items[5]}
            Icon={ICONS[5]}
          />
        </div>
      </div>
    </section>
  );
}

function Tile({ item, Icon, className = "", big = false, dark = false, bgImage, ...rest }) {
  return (
    <div
      {...rest}
      className={`group relative flex flex-col justify-between border ${
        dark ? "border-ink" : "border-rule"
      } ${dark ? "bg-ink text-paper" : "bg-sand"} hover:bg-ink hover:text-paper transition-colors duration-300 p-5 md:p-7 ${className}`}
    >
      {bgImage && (
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 group-hover:opacity-25 transition-opacity"
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "luminosity",
          }}
        />
      )}
      <div className="relative flex items-start justify-between">
        <span className={`font-mono text-[10px] tracking-[0.22em] uppercase ${dark ? "text-paper/80" : "text-mist"} group-hover:text-paper/80`}>
          {item.tag}
        </span>
        <Icon className={`h-5 w-5 ${dark ? "text-paper" : "text-ember"} group-hover:text-ember transition-colors`} />
      </div>
      <div className="relative mt-10">
        <h3 className={`font-serif ${big ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"} leading-[1.05] tracking-tight`}>
          {item.title}
        </h3>
        <p className={`mt-3 text-sm md:text-base max-w-md leading-relaxed ${dark ? "text-paper/85" : "text-mist"} group-hover:text-paper/85`}>
          {item.body}
        </p>
      </div>
    </div>
  );
}
